import AlertHelpIcon from '@assets/icons/alert-help.svg';
import Analytics from '@/utils/analytics';
import CreditRequestIcon from '@assets/icons/credit-request.svg';
import DuplicateIcon from '@assets/icons/duplicate.svg';
import i18n from '@/utils/i18n';
import InboxIcon from '@assets/icons/inbox.svg';
import InvoiceIcon from '@assets/icons/invoice.svg';
import MenuIcon from '@assets/icons/menu.svg';
import PaymentsIcon from '@assets/icons/payments.svg';
import PrinterIcon from '@assets/icons/printer.svg';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import RedoIcon from '@assets/icons/redo.svg';
import { ActivityIndicator } from '@/components/activity-indicator';
import {
  APP_CONTAINER_ROUTES,
  APP_MODAL_ROUTES,
  APP_ROUTES,
  CREDIT_REQUEST_INVOICE_ROUTES,
  LOCATION_MANAGER_ROUTES,
  PAYMENT_ROUTES,
} from '@/navigators/routes';
import { BaseActionSheet } from '@/components/base-action-sheet';
import { Button } from '@/components/button';
import {
  canDisplayETABanner,
  OrderETABanner,
} from '@/components/orders/order-eta-banner';
import { CheckInButton } from '@/components/check-in-button';
import { createStyles } from '@/contexts/theme-context';
import {
  CreditRequestByInvoice_SearchVariantPacksQuery,
  CreditRequestByInvoice_SearchVariantPacksQueryVariables,
  FulfillmentName,
  Order_PrintOrderTemplateQuery,
  Order_PrintOrderTemplateQueryVariables,
  OrderInvoiceLineItemFieldsFragment,
  OrderItemListFieldsFragment,
} from '@/types';
import {
  derivePartialPaymentAmount,
  displayInvoiceTotal,
  shouldShowInvoicePDFButton,
} from '@/utils/invoice';
import { displayTotalPrice, formatCurrency, OrderFee } from '@/utils/money';
import {
  FONTS,
  LIST_ITEM_SEPARATOR_HEIGHT,
  SCREEN_PADDING,
  SCREEN_PADDING_SMALL,
  TYPOGRAPHY,
} from '@/styles';
import { formatDate } from '@/utils/date';
import { getOrderDate, isPastDateOrToday } from '@/utils/order-helpers';
import { Invoice } from '@/components/invoice';
import { MultiStyleText } from '@/components/multi-style-text';
import { Props as NavigationHeaderContentProps } from '@/components/navigation-header-content';
import { OfflineComponent } from '@/components/offline-component';
import { OrderDeliveredAt } from '@/components/orders/order-delivered-at';
import { OrderDetailOriginalInboundModal } from './order-detail-original-inbound-modal';
import { OrderDetailPrices } from './order-detail-prices';
import { OrderDetailReorder } from './order-detail-reorder';
import { OrderFulfillmentDate } from '@/components/orders/order-fulfillment-date';
import { OrderInfo } from '@/components/orders/order-info';
import {
  OrderInvoiceAction,
  OrderInvoiceActionListOption,
  OrderInvoiceActions,
} from '@/components/orders/order-invoice-actions';
import { OrderInvoiceActionsSheet } from '@/components/orders/order-invoice-actions-sheet';
import { OrderItemDataType } from '@/components/orders/order-detail-list';
import { OrderReviewActionSheet } from '@/components/orders/order-review-action-sheet';
import { OrderStatus } from '@/enums';
import { OrderStatusLabel } from '@/components/orders/order-status-label';
import { PageContainer } from '@/components/page-container';
import { PaymentsStackParams } from '@/navigators/types';
import {
  Platform,
  StatusBar,
  StyleProp,
  Text,
  View,
  ViewStyle,
} from 'react-native';
import { PrintLabelConfirmation } from '@/components/print-label-confirmation';
import { RENDER_PRINT_ORDER_TEMPLATE_QUERY } from '@/components/tag-print-batch-queries';
import { SEARCH_VP_FOR_CREDIT_REQUEST_BY_INVOICE_QUERY } from '@/hooks/credit-requests/credit-request-queries';
import { SearchInput } from '@/components/search-input';
import { StackNavigationProp } from '@react-navigation/stack';
import { translateFulfillmentType } from '@domain/fulfillment-settings/utils/translate-fulfillment-type';
import { Type } from '@/components/toast';
import { Typography } from '@usepepper/ui/atoms/typography';
import { useAuthenticatedUserContext } from '@/contexts/user-context';
import {
  useDynamicConfigValue,
  useSupplierDynamicConfigValue,
} from '@/hooks/use-dynamic-config-value';
import { useGetOpenInvoicePDF } from '@/hooks/use-get-open-invoice-pdf';
import { useGetOrderForRestaurant } from '@/components/orders/hooks/use-get-order';
import { useItemQueryVariables } from '@/hooks/use-item-query-variables';
import { useLayoutContext } from '@/contexts/layout-context';
import { useLazyQuery } from '@/hooks/use-lazy-query';
import { useMultiSearchFilters } from '@/hooks/use-multi-search-filters';
import { useNavigation } from '@react-navigation/native';
import { useOfflineModeActive } from '@/hooks/use-offline-mode-active';
import { usePaymentsContext } from '@/contexts/payments-context';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useToasts } from '@/contexts/toast-context';
import { useWrappedQuery } from '@/hooks/use-wrapped-query';
import type { GetOrderSmartOrderMetadata } from '@/screens/order-inbounds/types';

export const REVIEWABLE_STATUSES = [
  OrderStatus.Delivered,
  OrderStatus.OutForDelivery,
  OrderStatus.ShouldBeDelivered,
];

interface Props {
  hideTitle?: boolean;
  invoiceButtonPosition: 'top' | 'bottom';
  navigationHeaderContainerStyle?: StyleProp<ViewStyle>;
  orderUUID: string;
  showBackButton?: boolean;
}

const getSearchableFields = (
  item: OrderInvoiceLineItemFieldsFragment | OrderItemDataType,
) => {
  if (item.__typename === 'order_invoice_line_items') {
    return [
      item.item_name ?? '',
      item.item_code,
      item.ship_erp_unit_of_measure,
    ];
  }
  const orderItem = item as OrderItemListFieldsFragment;
  return [
    orderItem.restaurant_display_name,
    orderItem.variants_pack.external_item_id ?? '',
    orderItem.pack.unit,
  ];
};

export const OrderDetailComponent: React.FC<Props> = ({
  hideTitle = false,
  invoiceButtonPosition,
  navigationHeaderContainerStyle,
  orderUUID,
  showBackButton = false,
}) => {
  const { bottom } = useSafeAreaInsets();
  const { isSmallLayout } = useLayoutContext();
  const { navigate } =
    useNavigation<StackNavigationProp<PaymentsStackParams>>();
  const {
    selectedRestaurantUUID,
    selectedRestaurantRelationship,
    supplierEmployeeRole,
  } = useAuthenticatedUserContext();
  const {
    appPaymentsEnabled,
    employeeRole,
    showPaymentsTab,
    displayItemPrices,
    name,
    showCreditRequest,
    supplierUUID,
    consumptionTax,
  } = selectedRestaurantRelationship;
  const { currencyCode, ftniEnabled, setPaymentContextState, useV2Invoices } =
    usePaymentsContext();
  const isOfflineModeActive = useOfflineModeActive();
  const [styles, theme] = useStyles();
  const itemQueryVariables = useItemQueryVariables();

  const [isOrderInboundModalOpen, setIsOrderInboundModalOpen] = useState(false);
  const checkInConfig = useDynamicConfigValue('check_in_config');
  const printOrderLabelEnabled = useDynamicConfigValue(
    'tag_printing_config',
  )?.order_printing_enabled;
  const invoicePDFVisibilityConfig = useDynamicConfigValue(
    'invoice_pdf_visibility_config',
  );
  const disablePepperInvoicePdf = useSupplierDynamicConfigValue(
    'disable_pepper_invoice_pdf',
    false,
  );
  const orderDuplicatingConfig = useDynamicConfigValue('order_copying_config');
  const { showToast } = useToasts();

  const { loading, data, refetch, hasResponse } = useGetOrderForRestaurant(
    orderUUID,
    selectedRestaurantRelationship.chatUUIDs[0],
    selectedRestaurantUUID,
    selectedRestaurantRelationship.supplierUUID,
  );

  const baseOrder = data?.getOrder?.order;
  const originalInbound =
    baseOrder?.smart_orders?.[0] || baseOrder?.parent_smart_orders?.[0] || null;
  const hasOriginalInbound = originalInbound && supplierEmployeeRole;

  const getOrderSmartOrderMetadata: GetOrderSmartOrderMetadata | null = useMemo(
    () =>
      hasOriginalInbound
        ? {
            mergeUUID: originalInbound.merge_uuid ?? undefined,
            threadId: originalInbound.thread_id ?? undefined,
            uuid: originalInbound.uuid,
          }
        : null,
    [hasOriginalInbound, originalInbound],
  );

  const [isReviewOpen, setReviewOpen] = useState(false);
  const [showMoreActionsSheet, setShowMoreActionsSheet] = useState(false);
  const [showReorderConfirmationSheet, setShowReorderConfirmationSheet] =
    useState(false);
  const [showOrderLabelConfirmation, setShowOrderLabelConfirmation] =
    useState(false);

  const order = data?.getOrder.order;
  const orderInvoiceByOrder = data?.getOrderInvoiceByOrder;
  const orderInvoice = orderInvoiceByOrder?.order_invoice;
  const orderFeesByOrder = data?.getOrderFees;
  const orderFees: OrderFee[] = useMemo(() => {
    return orderFeesByOrder || [];
  }, [orderFeesByOrder]);
  const existingCheckIn = orderInvoice?.order_invoice_check_in;
  const fulfillmentDisplayName =
    data?.getOrder.translated_fulfillment_display_name ?? null;

  const orderHasInvoice = orderInvoice && useV2Invoices;

  const isTodayOrPastOrder = useMemo(() => {
    if (order) {
      const orderDate = getOrderDate(order);
      return isPastDateOrToday(orderDate);
    }
    return false;
  }, [order]);

  const invoiceOrOrderItems = orderHasInvoice
    ? orderInvoice.order_invoice_line_items
    : (order?.orders_items ?? []);

  const orderDuplicatingEnabled = useMemo(() => {
    if (!orderDuplicatingConfig || isTodayOrPastOrder) {
      return false;
    }
    const { enabled_restaurant_roles, enabled_supplier_roles } =
      orderDuplicatingConfig;
    return (
      Boolean(
        employeeRole && enabled_restaurant_roles.includes(employeeRole),
      ) ||
      Boolean(
        supplierEmployeeRole &&
          enabled_supplier_roles.includes(supplierEmployeeRole),
      )
    );
  }, [
    employeeRole,
    isTodayOrPastOrder,
    orderDuplicatingConfig,
    supplierEmployeeRole,
  ]);

  const {
    clearSearch,
    filteredData,
    onDisplayTextChange,
    searchText,
    searching,
  } = useMultiSearchFilters<
    OrderInvoiceLineItemFieldsFragment | OrderItemDataType
  >({
    data: invoiceOrOrderItems,
    getSearchableFields,
  });

  const { data: creditRequestInvoiceItemsData } = useWrappedQuery<
    CreditRequestByInvoice_SearchVariantPacksQuery,
    CreditRequestByInvoice_SearchVariantPacksQueryVariables
  >(SEARCH_VP_FOR_CREDIT_REQUEST_BY_INVOICE_QUERY, {
    skip:
      !orderInvoice?.uuid ||
      !showCreditRequest ||
      !selectedRestaurantUUID ||
      !supplierUUID ||
      invoiceOrOrderItems.length === 0,
    variables: {
      ...itemQueryVariables,
      code: '', // because we are using orderInvoiceUUID
      orderInvoiceUUID: orderInvoice?.uuid,
      restaurantUUID: selectedRestaurantUUID,
      supplierUUID,
    },
  });

  const someItemsAreEligibleForCredit =
    creditRequestInvoiceItemsData?.searchVariantPacksForCreditRequest.some(
      (vp) => vp.eligible_for_credit,
    );

  const renderOrderTemplate = useLazyQuery<
    Order_PrintOrderTemplateQuery,
    Order_PrintOrderTemplateQueryVariables
  >(RENDER_PRINT_ORDER_TEMPLATE_QUERY, {
    fetchPolicy: 'network-only',
  });

  // cleanup the search when switching to a different order
  // only orderUUID dependency is intentional here
  useEffect(() => {
    clearSearch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderUUID]);

  // Invoices V2 doesn't insert all the same data into the DB as V1,
  // which means some quantity-selecting business logic doesn't work with V2
  const useInvoicePrices = Boolean(orderInvoice) && !useV2Invoices;

  const pricesPending = useMemo(() => {
    if (!displayItemPrices) {
      return true;
    }

    // If theres an existing invoice, and the distributor is on
    // Invoice V2, then we'll use the invoice line item sum
    if (orderHasInvoice) {
      return false;
    }

    const orderItems = order?.orders_items ?? [];
    const everythingHasPrice = orderItems.every((orderItem) => {
      const price = orderInvoice
        ? orderItem.order_item_prices?.unit_price_at_invoice_micros
        : orderItem.order_item_prices?.unit_price_at_order_micros;

      return typeof price === 'number';
    });

    return !everythingHasPrice;
  }, [displayItemPrices, orderHasInvoice, order?.orders_items, orderInvoice]);

  const totalPrice = useMemo(() => {
    if (!order) {
      return '';
    }

    // On Invoice V2, once there's an invoice associated with the order,
    // we want to sum the invoice line items and use that as the total
    // to account for any changes in prices, fees, or shorts.
    if (orderHasInvoice) {
      return displayInvoiceTotal(
        orderInvoice.order_invoice_line_items,
        currencyCode,
        consumptionTax,
      );
    }

    return displayTotalPrice(
      {
        orderFees,
        orderItems: order.orders_items,
      },
      {
        displayItemPrices,
        hideMissingItems: Boolean(orderInvoice),
      },
    );
  }, [
    currencyCode,
    displayItemPrices,
    order,
    orderFees,
    consumptionTax,
    orderHasInvoice,
    orderInvoice,
  ]);

  const outstandingBalanceInMicros = useMemo(() => {
    if (!orderInvoice) {
      return null;
    }

    const balance = orderInvoice?.order_invoice_status?.use_pepper_balance
      ? orderInvoice.order_invoice_status.balance_in_micros
      : orderInvoice?.erp_balance_in_micros;

    return typeof balance === 'number' && balance > 0 ? balance : null;
  }, [orderInvoice]);

  const outstandingBalance = useMemo(() => {
    if (outstandingBalanceInMicros === null) {
      return undefined;
    }
    return formatCurrency(outstandingBalanceInMicros, currencyCode);
  }, [currencyCode, outstandingBalanceInMicros]);

  const hasOutstandingBalance = Boolean(outstandingBalance);

  const partialPaymentAmountInMicros = useMemo(() => {
    return derivePartialPaymentAmount(orderInvoice, outstandingBalanceInMicros);
  }, [orderInvoice, outstandingBalanceInMicros]);

  const partialPaymentAmount = useMemo(() => {
    if (partialPaymentAmountInMicros === null) {
      return null;
    }
    return formatCurrency(partialPaymentAmountInMicros, currencyCode);
  }, [currencyCode, partialPaymentAmountInMicros]);

  const orderFulfillmentType =
    (order?.fulfillment_type as FulfillmentName | undefined) ||
    FulfillmentName.Delivery;

  const isCreditable =
    Platform.OS !== 'web' &&
    showCreditRequest &&
    someItemsAreEligibleForCredit &&
    isTodayOrPastOrder;

  const isReviewable = useMemo(() => {
    return (
      order &&
      REVIEWABLE_STATUSES.includes(order.status as OrderStatus) &&
      !isCreditable
    );
  }, [isCreditable, order]);

  const customerFacingId = order?.customer_facing_id ?? '';

  const canPayInvoice =
    searchText.length === 0 &&
    orderInvoice &&
    hasOutstandingBalance &&
    showPaymentsTab &&
    appPaymentsEnabled &&
    employeeRole;

  // Disable payment button on order details page if distributor is using FTNI payments
  const showPaymentButton = !ftniEnabled;

  const openInvoicePDF = useGetOpenInvoicePDF(
    orderInvoiceByOrder?.public_url,
    orderInvoice?.uuid,
    orderInvoice?.seen,
  );

  const onViewOriginalInbound = useCallback(() => {
    if (!getOrderSmartOrderMetadata) {
      return;
    }

    if (isSmallLayout) {
      navigate(APP_CONTAINER_ROUTES.OriginalInbound, {
        filterBySubmittedOrderUUID: orderUUID,
        orderMetadata: getOrderSmartOrderMetadata,
      });
    } else {
      setIsOrderInboundModalOpen(true);
    }
  }, [isSmallLayout, navigate, getOrderSmartOrderMetadata, orderUUID]);

  const onPayInvoice = useCallback(() => {
    if (showPaymentsTab && appPaymentsEnabled) {
      setPaymentContextState({
        autoShowModal: true,
        selectedInvoiceUUIDs: new Set([orderInvoice!.uuid]),
      });
      navigate(APP_ROUTES.Payments, { screen: PAYMENT_ROUTES.Base });
    }
  }, [
    showPaymentsTab,
    appPaymentsEnabled,
    setPaymentContextState,
    orderInvoice,
    navigate,
  ]);

  const shouldShowPDFButton = useMemo(() => {
    return shouldShowInvoicePDFButton({
      config: invoicePDFVisibilityConfig ?? null,
      disablePepperInvoicePdf: Boolean(disablePepperInvoicePdf),
      hasCustomPdf: Boolean(orderInvoice?.document_url),
      operatorEmployeeRole: employeeRole,
      supplierEmployeeRole,
    });
  }, [
    disablePepperInvoicePdf,
    employeeRole,
    invoicePDFVisibilityConfig,
    orderInvoice?.document_url,
    supplierEmployeeRole,
  ]);

  const moreActions: OrderInvoiceActionListOption[] = useMemo(() => {
    const actions: OrderInvoiceActionListOption[] = [
      {
        metadata: {
          Icon: RedoIcon,
          iconProps: {
            stroke: theme.sk.color.icon.primary,
          },
          onPress: () => setShowReorderConfirmationSheet(true),
          stackedSheet: true,
        },
        title: i18n.t('reorder.title'),
        value: OrderInvoiceAction.REORDER_ALL_ITEMS,
      },
    ];

    if (orderDuplicatingEnabled) {
      actions.push({
        metadata: {
          Icon: DuplicateIcon,
          iconProps: {
            stroke: theme.sk.color.icon.primary,
          },
          onPress: () => {
            Analytics.track('order_detail.duplicate_order_to', {
              orderUUID,
            });
            navigate(APP_MODAL_ROUTES.LocationManager, {
              params: { orderUUID, restaurantName: name },
              screen: LOCATION_MANAGER_ROUTES.DuplicateOrder,
            });
          },
          stackedSheet: true,
        },
        title: i18n.t('duplicate_order_to'),
        value: OrderInvoiceAction.DUPLICATE_ORDER_TO,
      });
    }

    if (hasOriginalInbound) {
      actions.push({
        metadata: {
          Icon: InboxIcon,
          iconProps: {
            stroke: theme.sk.color.icon.primary,
          },
          onPress: () => {
            Analytics.track('order_detail.original_inbound', {
              orderUUID,
            });
            onViewOriginalInbound();
          },
          stackedSheet: true,
        },
        title: i18n.t('view_original_inbound'),
        value: OrderInvoiceAction.VIEW_ORIGINAL_INBOUND,
      });
    }

    if (printOrderLabelEnabled && Platform.OS !== 'web') {
      actions.push({
        metadata: {
          Icon: PrinterIcon,
          iconProps: {
            stroke: theme.sk.color.icon.primary,
          },
          onPress: () => setShowOrderLabelConfirmation(true),
          stackedSheet: true,
        },
        title: i18n.t('send_to_mobile_printer'),
        value: OrderInvoiceAction.PRINT_ORDER_LABEL,
      });
    }

    if (orderInvoice) {
      if (shouldShowPDFButton) {
        actions.push({
          metadata: {
            Icon: InvoiceIcon,
            iconProps: {
              stroke: theme.sk.color.icon.primary,
            },
            onPress: async () => {
              Analytics.track('order_detail.view_pdf_button', {
                orderInvoiceUUID: orderInvoice.uuid,
              });
              await openInvoicePDF();
            },
            stackedSheet: false,
          },
          title: i18n.t('view_invoice_pdf'),
          value: OrderInvoiceAction.VIEW_INVOICE_PDF,
        });
      }

      if (isReviewable) {
        actions.push({
          metadata: {
            Icon: AlertHelpIcon,
            iconProps: {
              stroke: theme.sk.color.icon.primary,
            },
            onPress: () => {
              Analytics.track('order_detail.report', {
                orderUUID,
              });
              setReviewOpen(true);
            },
            stackedSheet: true,
          },
          title: i18n.t('something_wrong'),
          value: OrderInvoiceAction.REVIEW_ORDER,
        });
      }

      if (canPayInvoice && showPaymentButton) {
        actions.push({
          metadata: {
            Icon: PaymentsIcon,
            iconProps: {
              stroke: theme.sk.color.icon.primary,
            },
            onPress: () => {
              Analytics.track('order_detail.pay_invoice_button', {
                orderUUID,
              });
              onPayInvoice();
            },
            stackedSheet: true,
          },
          title: i18n.t('pay_invoice'),
          value: OrderInvoiceAction.PAY_INVOICE,
        });
      }
    }

    if (isCreditable) {
      actions.push({
        metadata: {
          Icon: CreditRequestIcon,
          iconProps: {
            stroke: theme.sk.color.icon.primary,
          },
          onPress: () => {
            navigate(APP_CONTAINER_ROUTES.CreditRequestInvoiceItems, {
              params: {
                orderInvoiceUUID: orderInvoice?.uuid,
                orderUUID,
                title: i18n.t('invoice_number', { number: customerFacingId }),
              },
              screen: CREDIT_REQUEST_INVOICE_ROUTES.CreditRequestInvoiceItems,
            });
          },
          stackedSheet: true,
        },
        title: i18n.t('credit_request'),
        value: OrderInvoiceAction.CREDIT_REQUEST,
      });
    }

    return actions;
  }, [
    canPayInvoice,
    customerFacingId,
    hasOriginalInbound,
    isCreditable,
    isReviewable,
    name,
    navigate,
    onPayInvoice,
    onViewOriginalInbound,
    openInvoicePDF,
    orderDuplicatingEnabled,
    orderInvoice,
    orderUUID,
    printOrderLabelEnabled,
    shouldShowPDFButton,
    showPaymentButton,
    theme.sk.color.icon.primary,
  ]);

  const paymentButton = useMemo(() => {
    return showPaymentButton ? (
      <Button
        onPress={onPayInvoice}
        trackingData={{
          name: 'order_detail.pay_invoice_button',
          properties: {
            orderUUID,
          },
        }}
      >
        {i18n.t('pay_invoice')}
      </Button>
    ) : null;
  }, [showPaymentButton, onPayInvoice, orderUUID]);

  const showCheckInStatusLabel =
    isTodayOrPastOrder &&
    Platform.OS !== 'web' &&
    checkInConfig?.check_in_enabled &&
    orderHasInvoice;
  const showCheckInEntry = searchText.length === 0 && showCheckInStatusLabel;

  const navigationHeaderProps = useMemo((): NavigationHeaderContentProps => {
    if (loading || !order) {
      return {
        showBackButton: showBackButton || isSmallLayout,
        showShadow: false,
        title: !isOfflineModeActive ? (
          <View style={styles.loadingContainer}>
            <ActivityIndicator testID="loading-indicator" />
          </View>
        ) : null,
      };
    }

    const orderStatusText = [];
    if (orderInvoice?.invoice_number) {
      orderStatusText.push(
        <Text key="invoice_number">
          {i18n.t('invoice_number', {
            number: orderInvoice.invoice_number,
          })}
        </Text>,
      );
    }
    if (orderInvoice?.due_at && hasOutstandingBalance) {
      orderStatusText.push(
        <Text key="payment_pay_by_date" style={styles.payByText}>
          {` • ${i18n.t('payment_pay_by_date', {
            date: formatDate(orderInvoice.due_at),
          })}`}
        </Text>,
      );
    }

    const orderETAComponent = canDisplayETABanner(order) ? (
      <View style={styles.orderETA}>
        <OrderETABanner orderTracking={order.order_tracking!} />
      </View>
    ) : null;

    const deliveredComponent = order.order_tracking
      ?.order_completed_delivery_datetime ? (
      <OrderDeliveredAt
        deliveredAt={order.order_tracking?.order_completed_delivery_datetime}
      />
    ) : null;

    const orderPlacedBy = order.placed_by
      ? {
          name:
            order.placed_by.supplier_employee_overrides[0]?.name ??
            order.placed_by.name,
          uuid: order.placed_by.uuid,
        }
      : null;

    const orderUnitCount = order.orders_items.reduce(
      (result, oi) =>
        result +
        (oi.order_item_prices?.pack_quantity_at_invoice ??
          oi.order_item_prices?.pack_quantity_at_order ??
          0),
      0,
    );

    if (isSmallLayout) {
      return {
        actions: [
          {
            Icon: MenuIcon,
            iconProps: {
              testID: 'order-detail-menu-button',
            },
            onPress: () => {
              Analytics.track('order_detail.menu_button.press');
              setShowMoreActionsSheet(true);
            },
          },
        ],
        centerTitle: isSmallLayout,
        contentFooter: (
          <>
            <View style={styles.fulfillmentInfoContainer}>
              <View style={styles.fulfillmentInfo}>
                <OrderFulfillmentDate
                  dateTranslator={(orderDate) =>
                    translateFulfillmentType(
                      orderFulfillmentType,
                      'styled_fulfillment_on',
                      fulfillmentDisplayName,
                      orderDate,
                    )
                  }
                  order={order}
                  textStyle={[TYPOGRAPHY.h2, styles.fulfillmentDate]}
                />
                <OrderStatusLabel
                  additionalText={
                    orderStatusText.length ? orderStatusText : undefined
                  }
                  existingCheckIn={existingCheckIn}
                  labelStyle={[TYPOGRAPHY.medium, styles.orderSubtitleText]}
                  order={order}
                  showCheckInStatusLabel={showCheckInStatusLabel}
                />
                <OrderInfo
                  orderName={order.storefront_metadata?.name}
                  placedAt={order.placed_at}
                  placedBy={orderPlacedBy}
                />
                {deliveredComponent}
              </View>
              {orderETAComponent}
              <View style={styles.searchWrapper}>
                <SearchInput
                  autoCorrect={false}
                  containerStyle={styles.searchContainer}
                  isLoading={searching}
                  onChangeText={onDisplayTextChange}
                  placeholder={i18n.t('order_search_placeholder')}
                  value={searchText}
                />
              </View>
              {displayItemPrices && searchText.length === 0 ? (
                <View style={styles.totalRow} testID="total-row">
                  <MultiStyleText
                    defaultStyle={styles.totalStandard}
                    emphasisStyle={styles.totalEmphasis}
                    forceSingleLine
                  >
                    {i18n.t(
                      orderUnitCount === 1
                        ? 'unit_count_emphasis'
                        : 'unit_count_emphasis_plural',
                      { count: orderUnitCount },
                    )}
                  </MultiStyleText>
                  {pricesPending ? (
                    <Text
                      style={[TYPOGRAPHY.small, styles.finalPriceConfirmed]}
                    >
                      {i18n.t('final_price_pending')}
                    </Text>
                  ) : (
                    <Text style={styles.totalPrice}>{totalPrice}</Text>
                  )}
                </View>
              ) : null}
            </View>
          </>
        ),
        showBackButton: isSmallLayout,
        title: hideTitle ? null : (
          <Text style={[TYPOGRAPHY.navigationCenterTitle, styles.titleLeft]}>
            {i18n.t('order_title_next', {
              customerFacingId,
            })}
          </Text>
        ),
      };
    }

    return {
      actions: [
        {
          component: <OrderInvoiceActions actions={moreActions} />,
        },
      ],
      contentContainerStyle: navigationHeaderContainerStyle,
      contentFooter: (
        <>
          <View style={styles.searchWrapperLarge}>
            <SearchInput
              autoCorrect={false}
              containerStyle={styles.searchContainer}
              isLoading={searching}
              onChangeText={onDisplayTextChange}
              placeholder={i18n.t('order_search_placeholder')}
              value={searchText}
            />
          </View>
          {displayItemPrices && searchText.length === 0 ? (
            <View
              style={styles.navigationHeaderContentFooterLarge}
              testID="order-detail-summary"
            >
              <View collapsable={false}>
                {orderUnitCount ? (
                  <>
                    <Text style={TYPOGRAPHY.small}>{i18n.t('units')}</Text>
                    <Text style={styles.orderUnitCount}>{orderUnitCount}</Text>
                  </>
                ) : null}
              </View>
              <OrderDetailPrices
                outstandingBalance={outstandingBalance}
                partialPaymentAmount={partialPaymentAmount}
                totalPrice={pricesPending ? undefined : totalPrice}
              />
            </View>
          ) : null}
        </>
      ),
      contentStyle: styles.navigationHeaderContentLarge,
      showBackButton,
      title: (
        <View style={styles.navigationHeaderTitleLarge}>
          <View collapsable={false}>
            {hideTitle ? null : (
              <Text style={[TYPOGRAPHY.h3, styles.orderNumberMarginLarge]}>
                {i18n.t('order_title_next', {
                  customerFacingId,
                })}
              </Text>
            )}
            <OrderFulfillmentDate
              dateTranslator={(orderDate) =>
                translateFulfillmentType(
                  orderFulfillmentType,
                  'styled_fulfillment_on',
                  fulfillmentDisplayName,
                  orderDate,
                )
              }
              order={order}
              textStyle={[TYPOGRAPHY.h2, styles.fulfillmentDate]}
            />
            <OrderStatusLabel
              additionalText={
                orderInvoice?.invoice_number
                  ? i18n.t('invoice_number', {
                      number: orderInvoice.invoice_number,
                    })
                  : undefined
              }
              existingCheckIn={existingCheckIn}
              labelStyle={[TYPOGRAPHY.medium, styles.orderSubtitleText]}
              order={order}
              showCheckInStatusLabel={showCheckInStatusLabel}
            />
            <OrderInfo
              orderName={order.storefront_metadata?.name}
              placedAt={order.placed_at}
              placedBy={orderPlacedBy}
            />
            {deliveredComponent}
            {orderETAComponent}
          </View>
        </View>
      ),
    };
  }, [
    loading,
    order,
    orderInvoice,
    hasOutstandingBalance,
    styles,
    isSmallLayout,
    moreActions,
    navigationHeaderContainerStyle,
    searching,
    onDisplayTextChange,
    searchText,
    displayItemPrices,
    outstandingBalance,
    partialPaymentAmount,
    pricesPending,
    totalPrice,
    showBackButton,
    hideTitle,
    customerFacingId,
    existingCheckIn,
    showCheckInStatusLabel,
    isOfflineModeActive,
    orderFulfillmentType,
    fulfillmentDisplayName,
  ]);

  const firstItemHasSubstitution = order?.orders_items?.[0]?.substitution;

  const showPayInvoiceFooter = Boolean(
    invoiceButtonPosition === 'bottom' && canPayInvoice,
  );

  const showFooter = showPayInvoiceFooter || showCheckInEntry;

  return (
    <>
      <StatusBar backgroundColor={theme.colors.white} barStyle="dark-content" />
      <PageContainer
        animateNavigationHeader
        navigationHeaderProps={navigationHeaderProps}
      >
        {(scrollViewProps) => (
          <>
            {loading ? (
              <View
                style={[
                  scrollViewProps.contentContainerStyle,
                  styles.staticContentContainer,
                ]}
              >
                <ActivityIndicator testID="loading-indicator" />
              </View>
            ) : !hasResponse && isOfflineModeActive ? (
              <View
                style={[
                  scrollViewProps.contentContainerStyle,
                  styles.staticContentContainer,
                ]}
              >
                <OfflineComponent />
              </View>
            ) : order ? (
              <View
                style={[
                  styles.listContainer,
                  firstItemHasSubstitution
                    ? styles.listContainerSubstitutionMargin
                    : null,
                ]}
              >
                <Invoice
                  displayItemPrices={displayItemPrices}
                  filteredData={filteredData}
                  isReviewable={isReviewable}
                  isV2Invoice={useV2Invoices}
                  onPressReviewButton={() => setReviewOpen(true)}
                  order={order}
                  orderFees={orderFees}
                  orderInvoice={orderInvoice}
                  scrollViewProps={scrollViewProps}
                  searchTerm={searchText}
                />
                {isReviewOpen ? (
                  <OrderReviewActionSheet
                    displayItemPrices={displayItemPrices}
                    onClose={() => setReviewOpen(false)}
                    onOrderRated={async () => {
                      await refetch();
                      setReviewOpen(false);
                    }}
                    order={order}
                    useInvoicePrices={useInvoicePrices}
                  />
                ) : null}
                {showMoreActionsSheet && isSmallLayout ? (
                  <OrderInvoiceActionsSheet
                    actions={moreActions}
                    onClose={() => setShowMoreActionsSheet(false)}
                  />
                ) : null}
                {showFooter ? (
                  <View
                    style={[
                      styles.invoiceBottomWrapper,
                      {
                        paddingBottom: Math.max(bottom, SCREEN_PADDING),
                      },
                    ]}
                    testID="outstanding-row"
                  >
                    {showCheckInEntry ? (
                      <CheckInButton
                        existingCheckIn={existingCheckIn}
                        invoiceUUID={orderInvoice.uuid}
                        orderUUID={orderUUID}
                        trackingData={{
                          name: 'order_detail.check_in_button',
                          properties: {
                            orderUUID,
                          },
                        }}
                      />
                    ) : showPayInvoiceFooter ? (
                      <>
                        <View style={styles.orderSummaryRows}>
                          <View style={styles.orderSummaryRow}>
                            <Typography variant="paragraph-base">
                              {i18n.t('outstanding_balance')}
                            </Typography>
                            <Typography variant="heading-md">
                              {outstandingBalance}
                            </Typography>
                          </View>
                          {partialPaymentAmount ? (
                            <View style={styles.orderSummaryRow}>
                              <Typography
                                color={theme.sk.color.text.tertiary}
                                variant="paragraph-md"
                              >
                                {i18n.t('partial_payment')}
                              </Typography>
                              <Typography
                                color={theme.sk.color.text.tertiary}
                                variant="paragraph-md"
                              >
                                {partialPaymentAmount}
                              </Typography>
                            </View>
                          ) : null}
                        </View>
                        {paymentButton}
                      </>
                    ) : null}
                  </View>
                ) : null}
              </View>
            ) : null}
          </>
        )}
      </PageContainer>
      {!hasOriginalInbound ||
      !isOrderInboundModalOpen ||
      !getOrderSmartOrderMetadata ? null : (
        <OrderDetailOriginalInboundModal
          filterBySubmittedOrderUUID={orderUUID}
          onClose={() => setIsOrderInboundModalOpen(false)}
          orderMetadata={getOrderSmartOrderMetadata}
        />
      )}
      {showReorderConfirmationSheet ? (
        <OrderDetailReorder
          onClose={() => setShowReorderConfirmationSheet(false)}
          orderUUID={orderUUID}
        />
      ) : null}
      {showOrderLabelConfirmation ? (
        <BaseActionSheet
          isVisible
          onClose={() => setShowOrderLabelConfirmation(false)}
        >
          <PrintLabelConfirmation
            buttonText={i18n.t('print_order')}
            onClose={() => setShowOrderLabelConfirmation(false)}
            onPrint={() =>
              showToast({
                content: i18n.t('order_sent_to_printer'),
                type: Type.POSITIVE,
              })
            }
            renderTemplate={async () => {
              const { data } = await renderOrderTemplate({
                variables: {
                  orderUUID,
                  restaurantUUID: selectedRestaurantUUID,
                  supplierUUID: selectedRestaurantRelationship.supplierUUID,
                },
              });

              return data?.renderPrintOrderTemplate.rendered_template;
            }}
          />
        </BaseActionSheet>
      ) : null}
    </>
  );
};

const useStyles = createStyles((theme) => ({
  finalPriceConfirmed: {
    color: theme.colors.inactiveText,
    flexBasis: '40%',
    textAlign: 'right',
  },
  fulfillmentDate: {
    marginBottom: 8,
  },
  fulfillmentInfo: {
    paddingHorizontal: 8,
    paddingTop: 8,
  },
  fulfillmentInfoContainer: {
    paddingBottom: 12,
  },
  invoiceBottomWrapper: {
    backgroundColor: theme.colors.white,
    paddingHorizontal: SCREEN_PADDING_SMALL,
    paddingTop: SCREEN_PADDING_SMALL,
  },
  listContainer: {
    flex: 1,
  },
  listContainerSubstitutionMargin: {
    marginTop: 16,
  },
  loadingContainer: {
    left: 0,
    position: 'absolute',
    right: 0,
    top: SCREEN_PADDING,
  },
  navigationHeaderContentFooterLarge: {
    alignItems: 'center',
    borderTopColor: theme.colors.stroke,
    borderTopWidth: LIST_ITEM_SEPARATOR_HEIGHT,
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  navigationHeaderContentLarge: {
    height: 'auto',
  },
  navigationHeaderTitleLarge: {
    flex: 1,
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
    justifyContent: 'space-between',
    paddingBottom: 8,
  },
  orderETA: {
    marginTop: 8,
  },
  orderNumberMarginLarge: {
    marginTop: 8,
  },
  orderSubtitleText: {
    ...FONTS.primarySemiBold,
    color: theme.colors.inactiveText,
  },
  orderSummaryRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  orderSummaryRows: {
    paddingVertical: theme.sk.spacing.spaceLarge,
  },
  orderUnitCount: {
    ...FONTS.primarySemiBold,
    fontSize: 20,
    lineHeight: 24,
  },
  payByText: {
    textTransform: 'none',
  },
  searchContainer: {
    backgroundColor: theme.colors.background,
    height: 48,
  },
  searchWrapper: {
    paddingTop: 8,
  },
  searchWrapperLarge: {
    paddingBottom: 16,
    paddingTop: 4,
  },
  staticContentContainer: {
    flex: 1,
    justifyContent: 'center',
  },
  titleLeft: {
    marginLeft: 8,
  },
  totalEmphasis: {
    ...FONTS.primarySemiBold,
    fontSize: 22,
    lineHeight: 26,
  },
  totalPrice: {
    ...FONTS.primaryBold,
    fontSize: 22,
    lineHeight: 26,
  },
  totalRow: {
    alignItems: 'center',
    backgroundColor: theme.colors.white,
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginHorizontal: 4,
    paddingHorizontal: 4,
    paddingTop: 12,
  },
  totalStandard: {
    ...FONTS.primary,
    color: theme.colors.primary,
    fontSize: 22,
    lineHeight: 24,
  },
}));
